import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileSpreadsheet, Upload, Download, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { parseCompanyExcel, bulkCreateCompanies, downloadExcelTemplate } from "@/lib/api";
import { toast } from "sonner";

export default function ImportCompanies() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsed, setParsed] = useState([]);
  const [selected, setSelected] = useState({});

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParsed([]);
    setSelected({});
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await parseCompanyExcel(fd);
      const list = res.data?.companies || [];
      setParsed(list);
      const initial = {};
      list.forEach((c, i) => { initial[i] = !c._already_exists; });
      setSelected(initial);
      const dups = list.filter((c) => c._already_exists).length;
      if (list.length === 0) {
        toast.error("No company rows found");
      } else {
        toast.success(`Parsed ${list.length} compan${list.length > 1 ? "ies" : "y"}${dups ? ` — ${dups} duplicate(s) auto-unchecked` : ""}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel parsing failed");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));

  const allOn = parsed.length > 0 && parsed.every((_, i) => selected[i]);
  const toggleAll = () => {
    const v = !allOn;
    const next = {};
    parsed.forEach((_, i) => { next[i] = v; });
    setSelected(next);
  };

  const importSelected = async () => {
    const picked = parsed.filter((_, i) => selected[i]).map(({ _already_exists, ...rest }) => rest);
    if (picked.length === 0) {
      toast.error("Select at least one company to import");
      return;
    }
    setSaving(true);
    try {
      const res = await bulkCreateCompanies({ companies: picked, skip_duplicates: true });
      const c = res.data?.created || 0;
      const s = res.data?.skipped || 0;
      toast.success(`Imported ${c} compan${c === 1 ? "y" : "ies"}${s ? ` · ${s} skipped (duplicate CIN)` : ""}`);
      navigate("/companies");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="import-companies-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Bulk · Multiple Companies</div>
          <h1 className="font-heading text-4xl font-light tracking-tight">Import Companies from Excel</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl leading-relaxed">
            Upload an .xlsx with 3 sheets — <strong>Companies</strong>, <strong>Directors</strong>, <strong>Members</strong> — linked by CIN. Preview the rows, uncheck what you don't want, then import.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await downloadExcelTemplate();
              toast.success("Template downloaded");
            } catch {
              toast.error("Download failed");
            }
          }}
          className="swiss-btn-ghost"
          data-testid="download-template"
        >
          <Download size={14} /> Download Template
        </button>
      </div>

      {parsed.length === 0 ? (
        <label className="dropzone block p-16 text-center" data-testid="bulk-dropzone">
          {parsing ? (
            <div className="flex flex-col items-center gap-3 text-zinc-600">
              <Loader2 size={28} className="animate-spin text-[#002FA7]" />
              <span className="font-medium">Parsing Excel…</span>
            </div>
          ) : (
            <>
              <FileSpreadsheet size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-3" />
              <div className="font-heading text-xl font-medium mb-1">Drop your .xlsx file</div>
              <div className="text-sm text-zinc-500 mb-4">Max 500 companies per file. 10 MB limit.</div>
              <span className="swiss-btn-primary">
                <Upload size={14} /> Choose Excel File
              </span>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleFile} data-testid="bulk-file-input" />
            </>
          )}
        </label>
      ) : (
        <div className="border border-zinc-200 bg-white" data-testid="preview-table">
          <div className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="font-mono-plex text-xs text-zinc-500">{parsed.length} rows parsed</span>
              <span className="font-mono-plex text-xs text-[#002FA7]">{selectedCount} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={toggleAll} className="text-xs text-zinc-600 hover:text-[#002FA7]" data-testid="toggle-all">
                {allOn ? "Deselect All" : "Select All"}
              </button>
              <button
                onClick={() => { setParsed([]); setSelected({}); }}
                className="swiss-btn-ghost"
                data-testid="discard-preview"
              >
                Discard
              </button>
              <button
                onClick={importSelected}
                disabled={saving || selectedCount === 0}
                className="swiss-btn-primary"
                data-testid="import-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Importing…" : `Import ${selectedCount} Compan${selectedCount === 1 ? "y" : "ies"}`}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="preview-rows">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 font-medium text-zinc-700">Company Name</th>
                  <th className="px-3 py-2 font-medium text-zinc-700">CIN</th>
                  <th className="px-3 py-2 font-medium text-zinc-700">Type</th>
                  <th className="px-3 py-2 font-medium text-zinc-700 text-right">Directors</th>
                  <th className="px-3 py-2 font-medium text-zinc-700 text-right">Members</th>
                  <th className="px-3 py-2 font-medium text-zinc-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {parsed.map((c, i) => (
                  <tr key={`${c.cin || c.name}-${i}`} className="hover:bg-zinc-50" data-testid={`row-${i}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={!!selected[i]}
                        onChange={() => toggle(i)}
                        className="accent-[#002FA7]"
                        data-testid={`row-check-${i}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 font-mono-plex text-xs text-zinc-600">{c.cin || "—"}</td>
                    <td className="px-3 py-2 text-zinc-700">{c.company_type}</td>
                    <td className="px-3 py-2 text-right font-mono-plex text-zinc-700">{c.directors?.length || 0}</td>
                    <td className="px-3 py-2 text-right font-mono-plex text-zinc-700">{c.members?.length || 0}</td>
                    <td className="px-3 py-2">
                      {c._already_exists ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-amber-700">
                          <AlertTriangle size={11} /> Duplicate CIN
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-emerald-700">
                          <CheckCircle2 size={11} /> Ready
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 text-xs text-zinc-500 leading-relaxed">
        <strong>Template tips:</strong> Directors and Members sheets are optional. If included, link rows to companies via the <code className="bg-zinc-100 px-1">CIN</code> column. Column names are flexible — <code className="bg-zinc-100 px-1">Company Name</code>, <code className="bg-zinc-100 px-1">company_name</code>, and <code className="bg-zinc-100 px-1">NAME</code> all work.{" "}
        <Link to="/companies" className="text-[#002FA7] hover:underline">Back to Companies</Link>
      </div>
    </div>
  );
}
